<template>
  <div class="not-found">
    <div class="not-found-card">
      <!-- Illustration : un carton ouvert et vide, une loupe qui cherche -->
      <svg class="not-found-art" viewBox="0 0 240 170" aria-hidden="true">
        <ellipse cx="120" cy="156" rx="78" ry="8" class="art-shadow" />
        <g class="art-box">
          <path d="M60 78 L120 96 L180 78 L180 140 L120 156 L60 140 Z" class="art-box-front" />
          <path d="M60 78 L120 96 L120 156 L60 140 Z" class="art-box-side" />
          <path d="M60 78 L36 58 L96 40 L120 60 Z" class="art-flap" />
          <path d="M180 78 L204 58 L144 40 L120 60 Z" class="art-flap" />
          <path d="M60 78 L120 60 L180 78 L120 96 Z" class="art-box-inside" />
        </g>
        <g class="art-lens">
          <circle cx="172" cy="46" r="20" class="art-lens-glass" />
          <line x1="186" y1="60" x2="204" y2="78" class="art-lens-handle" />
        </g>
        <text x="148" y="122" class="art-question">?</text>
      </svg>

      <div class="not-found-code">404</div>
      <h1 class="not-found-title">{{ $t('page_not_found') }}</h1>
      <p class="not-found-text">{{ $t('page_not_found_text') }}</p>

      <div class="d-flex flex-wrap justify-content-center gap-3">
        <button type="button" class="btn btn-light" @click="goBack">
          <i class="bi bi-arrow-left me-2"></i>{{ $t('back') }}
        </button>
        <router-link to="/" class="btn btn-primary">
          <i class="bi bi-house-door me-2"></i>{{ $t('page_not_found_home') }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Page introuvable (toute adresse inconnue). Retour à la page précédente si l'on vient de
 * l'application, sinon à l'accueil.
 */
export default {
  name: 'NotFoundPage',
  methods: {
    goBack() {
      if (window.history.length > 1) this.$router.back()
      else this.$router.push('/')
    },
  },
}
</script>

<style scoped>
/* même couleur que les boutons : celle de la boutique une fois connecté, celle de la
   plateforme sur les pages publiques (body.platform-themed, voir usePlatformColor) */
.not-found {
  --nf: var(--kt-primary);
  --nf-rgb: var(--kt-primary-rgb);
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
}

/* fond sur tout l'écran, quelle que soit la hauteur du conteneur */
.not-found::before {
  content: '';
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background:
    radial-gradient(circle at 20% 20%, rgba(var(--nf-rgb), 0.08), transparent 45%),
    radial-gradient(circle at 80% 80%, rgba(var(--nf-rgb), 0.06), transparent 40%);
}

.not-found-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 520px;
  text-align: center;
  padding: 40px 32px 36px;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(15, 23, 42, 0.08);
}

.not-found-art {
  width: 220px;
  max-width: 70%;
  margin: 0 auto 8px;
  display: block;
}

.art-shadow { fill: rgba(15, 23, 42, 0.06); }
.art-box-front { fill: rgba(var(--nf-rgb), 0.18); }
.art-box-side { fill: rgba(var(--nf-rgb), 0.28); }
.art-box-inside { fill: rgba(var(--nf-rgb), 0.45); }
.art-flap { fill: rgba(var(--nf-rgb), 0.12); stroke: rgba(var(--nf-rgb), 0.3); stroke-width: 1.5; }
.art-lens-glass { fill: rgba(255, 255, 255, 0.85); stroke: var(--nf); stroke-width: 5; }
.art-lens-handle { stroke: var(--nf); stroke-width: 7; stroke-linecap: round; }
.art-question { fill: var(--nf); font-size: 34px; font-weight: 800; font-family: inherit; }

.art-lens {
  transform-origin: 172px 46px;
  animation: search 3.2s ease-in-out infinite;
}

@keyframes search {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  25% { transform: translate(-14px, 6px) rotate(-8deg); }
  50% { transform: translate(-26px, 0) rotate(0deg); }
  75% { transform: translate(-10px, -6px) rotate(6deg); }
}

@media (prefers-reduced-motion: reduce) {
  .art-lens { animation: none; }
}

.not-found-code {
  font-size: 72px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -2px;
  margin-bottom: 12px;
  color: var(--nf);
  background: linear-gradient(135deg, var(--nf), rgba(var(--nf-rgb), 0.55));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.not-found-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--bs-gray-900, #181c32);
  margin-bottom: 8px;
}

.not-found-text {
  font-size: 1rem;
  color: var(--bs-gray-600, #7e8299);
  margin-bottom: 28px;
}


@media (max-width: 575.98px) {
  .not-found-card { padding: 28px 20px; }
  .not-found-code { font-size: 56px; }
}
</style>

<style>
/* hors scoped : « :global(body.x) .y » de Vue ne garderait que body.x */
body.platform-themed .not-found {
  --nf: var(--platform-primary, var(--kt-primary));
  --nf-rgb: var(--platform-primary-rgb, var(--kt-primary-rgb));
}
</style>
